import React, { MouseEventHandler, useEffect, useMemo, useReducer, useState } from 'react'
import { useSelector } from 'react-redux'
import AddIcon from 'transfix-icons/build/AddIcon'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { useToastState, Toast } from 'transfix-ui/components/Toast'
import { makeStyles, Box, Grid, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { brandColors } from 'transfix-ui/theme/colors'
import { RATE_ACTION_TYPE } from 'types/carrier'
import { RoutingGuideEditComponent } from 'utils/routingGuides/LaneModal/RoutingGuideEditComponent'
import {
  EditLaneModalProvider,
  LaneRemoveModalProvider,
} from 'utils/routingGuides/LaneModal/contexts'
import { deleteRoutingGuide } from 'apps/carrier/utils/api/contractLanes'
import {
  selectCarrierId,
  selectCarrierName,
  selectCarrierState,
} from 'apps/carrier/utils/selectors'
import {
  useGetCarrierReservedLanes,
  RequestFilters,
  RoutingGuideRowProps,
} from 'apps/marketplace/apiTypes/carrierRoutingGuides'
import {
  laneModalReducer,
  laneModalInitialState,
  ReducerActions,
} from 'apps/marketplace/utils/reducers/contractLaneModal'
import { LaneRemoveModal } from './LaneRemoveModal'
import { RoutingGuidesTableEntries } from './RoutingGuidesTableEntries'

const useRoutingGuidesStyles = makeStyles({
  empty: {
    padding: '14px',
    textAlign: 'center',
    backgroundColor: brandColors.coolGray1,
    color: brandColors.coolGray5,
  },
})

export const RoutingGuides = () => {
  const carrierApplicationId: number | string | undefined = useSelector(selectCarrierId)
  const carrierApplicationName: string | undefined = useSelector(selectCarrierName)
  const carrierApplicationState: string | undefined = useSelector(selectCarrierState)
  const [editLaneModal, setEditLaneModal] = useReducer(laneModalReducer, laneModalInitialState)
  const [laneRemoveModal, setLaneRemoveModal] = useReducer(laneModalReducer, laneModalInitialState)
  const { currentContract, error, loading: laneRemoveLoading } = laneRemoveModal || {}
  const classes = useRoutingGuidesStyles()
  const { closeToast, openToast, toastMessage, toastType } = useToastState()

  const filters: RequestFilters = useMemo(
    () => ({
      carrierApplicationId,
      actionType: RATE_ACTION_TYPE.reserved,
      includes: ['dat_market_lane', 'carrier_application_offer_limits'],
    }),
    [carrierApplicationId]
  )

  const {
    data: rgData,
    error: rgError,
    loading: rgLoading,
    refetch: fetchRoutingGuides,
  } = useGetCarrierReservedLanes({
    lazy: true,
    queryParams: { filters },
  })

  const { carriercontractlanes: fetchedRoutingGuides, nextPage: hasMore = false } = rgData || {}

  /**
   * API doesn't return the current page nor the total amount of results, so
   * keep track of that state here
   */
  const [routingGuides, setRoutingGuides] = useState<RoutingGuideRowProps[]>([])
  const [lastFetchedPage, setLastFetchedPage] = useState<number>(-1)

  /**
   * Fetch the first page of routing guides
   */
  useEffect(() => {
    const fetchInitialRoutingGuides = async () => {
      await fetchRoutingGuides()
      setLastFetchedPage(0)
    }

    fetchInitialRoutingGuides()
  }, [fetchRoutingGuides])

  /**
   * Append the fetched routing guides to the routingGuides array only
   * when lastFetchedPage is incremented
   */
  useEffect(() => {
    if (Array.isArray(fetchedRoutingGuides) && lastFetchedPage >= 0) {
      setRoutingGuides(routingGuides => [...routingGuides, ...fetchedRoutingGuides])
    }
  }, [lastFetchedPage])

  /**
   * Fetch the next page of routing guides and increment lastFetchedPage
   */
  const handleLoadMoreClick: MouseEventHandler = async () => {
    const pageCount = lastFetchedPage + 1
    await fetchRoutingGuides({
      queryParams: {
        filters,
        pageCount,
      },
    })
    setLastFetchedPage(pageCount)
  }

  const handleAddRateClick: MouseEventHandler = () => {
    setEditLaneModal({ type: ReducerActions.show })
  }

  const carrier = {
    id: carrierApplicationId,
    legalName: carrierApplicationName,
    state: carrierApplicationState,
  }

  const closeLaneRemoveModalHandler = () =>
    setLaneRemoveModal({ type: ReducerActions.hide, currentContract: undefined })

  const editLaneSuccess = (routingGuide: RoutingGuideRowProps) => {
    setRoutingGuides(routingGuides => {
      const arr = [...routingGuides]
      const index = arr.findIndex(rg => rg.uuid === routingGuide.uuid)

      if (index >= 0) {
        // replace existing entry on edit
        arr.splice(index, 1, routingGuide)
        return arr
      }

      // add to end of array on create
      return [...arr, routingGuide]
    })
    openToast({ toastType: 'default', toastMessage: 'Success' })
  }

  const onRemoveLanesHandler = async (contractIds: number[]) => {
    setLaneRemoveModal({ type: ReducerActions.request })

    try {
      await deleteRoutingGuide({ contractIds })
      // filter out the lanes that were deleted
      setRoutingGuides(routingGuides =>
        routingGuides.filter(rg => !contractIds.includes(rg.contractId))
      )
      setLaneRemoveModal({ type: ReducerActions.success })
      closeLaneRemoveModalHandler()
      openToast({ toastType: 'default', toastMessage: 'Routing Guide Entry Successfully Removed' })
    } catch (err) {
      setLaneRemoveModal({ type: ReducerActions.error, errors: err })
    }
  }

  return (
    <Card dataTest='routing-guides'>
      {currentContract && (
        <LaneRemoveModal
          onRemoveLanes={onRemoveLanesHandler}
          onClose={closeLaneRemoveModalHandler}
          data={currentContract}
          deleting={laneRemoveLoading}
          error={error}
        />
      )}

      <Grid container spacing={2}>
        <Grid item xs={8}>
          <Box mb={4}>
            <Typography variant='h3'>Routing Guides Rates</Typography>
            <Box mt={2}>
              This carrier will be added to any Routing Guides that match the lane preference
              associated with their rate.
            </Box>
            <Box>They&apos;ll receive Reserved Loads at their rate.</Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box className='fr'>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddRateClick}
              data-test='add-routing-guides-button'>
              Add Routing Guide Rate
            </Button>
          </Box>
        </Grid>
      </Grid>

      <EditLaneModalProvider value={{ state: editLaneModal, dispatch: setEditLaneModal }}>
        <LaneRemoveModalProvider value={{ state: laneRemoveModal, dispatch: setLaneRemoveModal }}>
          {!rgError && routingGuides.length > 0 && (
            <RoutingGuidesTableEntries routingGuides={routingGuides} />
          )}
        </LaneRemoveModalProvider>
      </EditLaneModalProvider>

      {rgLoading && <Spinner />}

      {!rgError && hasMore && (
        <Box display='flex' justifyContent='center'>
          <Button onClick={handleLoadMoreClick} color='secondary' disabled={rgLoading}>
            Load More
          </Button>
        </Box>
      )}

      {routingGuides && !rgLoading && !rgError && routingGuides.length === 0 && (
        <Box className={classes.empty}>
          <Box>This carrier does not have any lane in the Routing Guides yet.</Box>
        </Box>
      )}

      {rgLoading && !rgError && (
        <Box display='flex' justifyContent='space-between'>
          <Skeleton variant='rectangular' width='24%' height='40px' />
          <Skeleton variant='rectangular' width='24%' height='40px' />
          <Skeleton variant='rectangular' width='24%' height='40px' />
          <Skeleton variant='rectangular' width='24%' height='40px' />
        </Box>
      )}

      {!rgLoading && rgError && (
        <InlineMessage message={rgError.message} type={INLINE_MESSAGE_VARIANTS.ALERT} />
      )}

      <EditLaneModalProvider value={{ state: editLaneModal, dispatch: setEditLaneModal }}>
        <RoutingGuideEditComponent
          carrier={carrier}
          creationSource='carrier_profile'
          dataOnSuccess={editLaneSuccess}
        />
      </EditLaneModalProvider>

      <Toast
        open={!!toastMessage}
        onClose={closeToast}
        message={toastMessage}
        variation={toastType}
      />
    </Card>
  )
}
